import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  ZONES,
  ARROW_POSITIONS,
  CLICKABLE_AREAS,
  SEDAN_IMAGE,
  buildWedgeStyle,
  buildRoofGlowStyle,
} from './damage/ZonePicker';
import { cn } from '../lib/utils';

const CAR_ZONES = ZONES.filter((zone) => zone.Icon);
const OTHER_ZONES = ZONES.filter((zone) => !zone.Icon);

// A zone the photo vocabulary does not name (set by ML, or by an older build)
// still gets a button — title-cased, so it reads like the rest.
const zoneLabel = (zoneId) =>
  ZONES.find((zone) => zone.id === zoneId)?.label
  || zoneId.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

/**
 * PhotoZoneFilter — the birds-eye car, as a filter for the Photos section.
 *
 * The same car and zones as ZonePicker (which assigns a photo its section) and
 * DamageHotspotSelector (which filters the Damage section). Sections holding
 * photos glow faintly; click one to show only its photos, click it again — or
 * All — to clear. Sections with no photos cannot be picked: filtering to an
 * empty grid answers nothing.
 */
const PhotoZoneFilter = ({ zonesWithPhotos = new Set(), value = null, onChange }) => {
  const [hoveredZone, setHoveredZone] = useState(null);

  const toggle = (zoneId) => onChange?.(value === zoneId ? null : zoneId);
  const knownIds = new Set(ZONES.map((zone) => zone.id));
  const otherZones = [
    ...OTHER_ZONES.filter((zone) => zonesWithPhotos.has(zone.id)).map((zone) => zone.id),
    ...[...zonesWithPhotos].filter((zoneId) => !knownIds.has(zoneId)).sort(),
  ];

  return (
    <div className="group/photo-filter">
      <div className="relative isolate w-full aspect-[2/3] overflow-visible">
        {/* Glow: faint where there are photos, strong for the active filter */}
        <div className="pointer-events-none absolute inset-[8%]">
          {CAR_ZONES.map((zone) => {
            const hasPhotos = zonesWithPhotos.has(zone.id);
            const intensity = value === zone.id
              ? 0.7
              : hasPhotos && hoveredZone === zone.id
                ? 0.5
                : hasPhotos
                  ? 0.25
                  : 0;
            return (
              <div
                key={`glow-${zone.id}`}
                className="absolute inset-0 transition-opacity duration-200 ease-out"
                style={zone.id === 'roof' ? buildRoofGlowStyle(intensity) : buildWedgeStyle(zone.angle, intensity, zone.id)}
              />
            );
          })}
        </div>

        <div className="absolute inset-[8%] overflow-hidden bg-transparent flex items-center justify-center">
          <img
            src={SEDAN_IMAGE}
            alt=""
            className="pointer-events-none object-contain"
            style={{ width: '70%', height: '70%' }}
          />
        </div>

        {CAR_ZONES.map((zone) => {
          const hasPhotos = zonesWithPhotos.has(zone.id);
          const area = CLICKABLE_AREAS[zone.id];
          const arrow = ARROW_POSITIONS[zone.id];
          const Icon = zone.Icon;
          return (
            <React.Fragment key={zone.id}>
              <button
                type="button"
                aria-label={zone.label}
                aria-pressed={value === zone.id}
                disabled={!hasPhotos}
                onClick={() => toggle(zone.id)}
                onMouseEnter={() => setHoveredZone(zone.id)}
                onMouseLeave={() => setHoveredZone(null)}
                className={cn('absolute', hasPhotos ? 'cursor-pointer' : 'cursor-default')}
                style={{ top: area.top, left: area.left, width: area.width, height: area.height }}
              />
              <div
                className={cn(
                  'pointer-events-none transition-all duration-200',
                  value === zone.id || (hasPhotos && hoveredZone === zone.id)
                    ? 'text-sky-500 opacity-100'
                    : hasPhotos
                      ? 'text-slate-400 opacity-0 group-hover/photo-filter:opacity-30'
                      : 'opacity-0'
                )}
                style={{ position: 'absolute', top: arrow.top, left: arrow.left, transform: 'translate(-50%, -50%)' }}
              >
                <Icon size={24} strokeWidth={2.5} />
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="mt-3 flex flex-col gap-1.5">
        {otherZones.map((zoneId) => (
          <Button
            key={zoneId}
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={value === zoneId}
            onClick={() => toggle(zoneId)}
            className={cn(
              'w-full text-xs transition-colors',
              value === zoneId
                ? 'bg-sky-100 border-sky-500 text-sky-700 hover:bg-sky-200'
                : 'text-slate-600 hover:text-sky-600 hover:border-sky-400'
            )}
          >
            {zoneLabel(zoneId)}
          </Button>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-pressed={value === null}
          onClick={() => onChange?.(null)}
          className={cn(
            'w-full text-xs transition-colors',
            value === null
              ? 'bg-sky-100 border-sky-500 text-sky-700 hover:bg-sky-200 font-medium'
              : 'text-slate-600 hover:text-sky-600 hover:border-sky-400'
          )}
        >
          All
        </Button>
      </div>
    </div>
  );
};

export default PhotoZoneFilter;
